import clsx from "clsx";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { isCalendarDayString } from "./dateTime";
import { store } from "../store/store";
import { useMemo } from "react";
import { useGetTaxSelectionQuery } from "../store/apiSlice/taxApiSlice";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

/**
 * Show a stored instant in the browser timezone.
 *
 * Naive `YYYY-MM-DD HH:mm:ss` strings are treated as UTC, matching the
 * previous moment.utc(...) callers.
 *
 * @param {string|number|Date|import("dayjs").Dayjs} timestamp
 * @return {import("dayjs").Dayjs}
 */
export const timeStampInCurrentTZ = timestamp => {
  if (timestamp == null || timestamp === "") {
    return dayjs(NaN);
  }
  if (dayjs.isDayjs(timestamp)) {
    return timestamp.tz(dayjs.tz.guess());
  }
  if (typeof timestamp?.valueOf === "function" && timestamp._isAMomentObject) {
    return dayjs(timestamp.valueOf()).tz(dayjs.tz.guess());
  }
  if (typeof timestamp === "string" && isCalendarDayString(timestamp)) {
    return dayjs(timestamp.trim(), "YYYY-MM-DD");
  }
  if (
    typeof timestamp === "string" &&
    !timestamp.includes("T") &&
    !timestamp.endsWith("Z")
  ) {
    return dayjs.utc(timestamp).tz(dayjs.tz.guess());
  }
  return dayjs(timestamp).tz(dayjs.tz.guess());
};

/**
 * Formats a given amount to a specified number of decimal places.
 * Allows toggling between rounded and precise values.
 * If the input is not a valid number, returns null.
 *
 * @param {number|string} amount - The amount to format.
 * @param {number} decimalPlaces - Number of decimal places (default is 2).
 * @param {boolean} round - Whether to round the number (default is false).
 * @returns {number|null} The formatted amount or null if input is invalid.
 */
export const formatAmount = (amount, decimalPlaces = 2, round = false) => {
  // Check if the amount is a number or can be converted to a number
  const numericAmount = Number(amount);
  if (isNaN(numericAmount)) {
    return null; // Return null for invalid inputs
  }

  if (round) {
    // If round is true, round to the nearest decimal place
    const factor = Math.pow(10, decimalPlaces);
    return Math.round(numericAmount * factor) / factor;
  } else {
    // If round is false, simply format to the specified decimal places
    return Number(numericAmount).toFixed(decimalPlaces);
  }
};

/**
 * Retrieves a nested value from an object based on a given path.
 *
 * @param {Object} obj - The object to retrieve the value from.
 * @param {string} path - A dot-separated string specifying the path to the nested value.
 * @returns {*} - The value found at the specified path, or undefined if the path is not valid.
 *
 * @example
 * const obj = { a: { b: { c: 1 } } };
 * const value = getNestedValue(obj, 'a.b.c'); // returns 1
 * const invalidValue = getNestedValue(obj, 'a.b.x'); // returns undefined
 */
export const getNestedValue = (obj, path) => {
  // Ensure the object is not null or undefined
  if (!obj) return undefined;

  return path.split(".").reduce((acc, part) => {
    // Check if the accumulator is an object and has the key
    return typeof acc === "object" && acc !== null ? acc[part] : undefined;
  }, obj);
};

export const consolidateErrors = errorsArray => {
  const errors = {};

  errorsArray.forEach(errorObj => {
    // Iterate over each object in the array
    Object.keys(errorObj).forEach(key => {
      // Check if the key already exists in the errors object
      if (!errors[key]) {
        errors[key] = new Set(); // Use a Set to ensure uniqueness
      }
      // Add the error message(s) to the Set
      errorObj[key].forEach(message => {
        errors[key].add(message);
      });
    });
  });

  // Convert Set back to array for easier usage in most UI frameworks
  Object.keys(errors).forEach(key => {
    errors[key] = Array.from(errors[key]);
  });

  return errors;
};

export const randomColor = () => {
  const colors = ["success", "secondary", "info", "warning", "primary", "danger"];

  // Generate a random color index
  const randomColorIndex = Math.floor(Math.random() * colors.length);

  // Use the random index to select a random element
  return colors[randomColorIndex];
};

function isSameDay(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function isYesterday(date, now) {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  return isSameDay(date, yesterday);
}

export function formatChatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();

  const timeOptions = { hour: "numeric", minute: "numeric", hour12: true };
  const dateOptions = { month: "short", day: "numeric", ...timeOptions };
  const fullDateOptions = { year: "numeric", ...dateOptions };

  if (isSameDay(date, now)) {
    // Today: show time only
    return date.toLocaleTimeString("en-US", timeOptions);
  } else if (isYesterday(date, now)) {
    // Yesterday: show 'Yesterday' with time
    const timeString = date.toLocaleTimeString("en-US", timeOptions);
    return `Yesterday, ${timeString}`;
  } else if (date < now) {
    // Before yesterday: show date and time
    return date.toLocaleDateString("en-US", dateOptions);
  } else {
    // Last year: show full date and time with year
    return date.toLocaleDateString("en-US", fullDateOptions);
  }
}

export function getTableColumns(columns, index) {
  if (!Array.isArray(columns)) return [];

  const addIndex = columns[0].id === "selection" && index > 0 ? 1 : 0;

  return columns.map((column, i) => {
    const shouldFix = i < index + addIndex;

    return {
      ...column,
      className: clsx(column.className, column.fixedPositionClass, {
        "bg-body position-sticky z-i": shouldFix,
      }),
    };
  });
}

/**
 * Flatten a table-cell value into unique string filter options.
 *
 * @param {*} value Cell value from react-table.
 * @return {!Array<string>}
 */
export const filterOptionStrings = value => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.flatMap(item => filterOptionStrings(item));
  }
  if (typeof value === "string" || typeof value === "number") {
    return [String(value)];
  }
  return [];
};

export const filterTypes = {
  multiSelect: (rows, columnId, filterValue) => {
    if (!Array.isArray(filterValue) || filterValue.length === 0) return rows;
    const set = new Set(filterValue);
    return rows.filter(row =>
      filterOptionStrings(row.values[columnId]).some(option => set.has(option)),
    );
  },
  zeroNonZero: (rows, id, filterValues) => {
    if (!Array.isArray(filterValues) || filterValues.length === 0) return rows;
    const selected = new Set(filterValues);
    return rows.filter(row => {
      const raw = row.values[id];
      const num = Number(raw);
      const isZero = !isNaN(num) && num === 0;
      const isNonZero = !isNaN(num) && num !== 0;
      return (selected.has("Zero") && isZero) || (selected.has("Non Zero") && isNonZero);
    });
  },
};

export const sortTypes = {};

/**
 * getPlacement
 *
 * Determine whether the popover should align to the start or end of the anchor
 * based on its horizontal position in the viewport.
 *
 * Params:
 * - anchorEl (HTMLElement): The element the popover is anchored to.
 *
 * Returns:
 * - string: "bottom-start" if the anchor is in the left half, otherwise "bottom-end".
 */
export const getPlacement = anchorEl => {
  if (!anchorEl) return "bottom-start";
  const rect = anchorEl.getBoundingClientRect();
  const midX = rect.left + rect.width / 2;
  return midX < window.innerWidth / 2 ? "bottom-start" : "bottom-end";
};

export function formatAmountWithDefaults(amount, precisionOverride = null, roundOverride = null) {
  if (amount == null) return null;

  // 🗄️ pull defaults from Redux
  const { precision, round } = store.getState().misc.settings;

  const effectivePrecision = precisionOverride ?? precision;
  const effectiveRound = roundOverride ?? round;

  return Number(formatAmount(amount, effectivePrecision, effectiveRound));
}

const lineDiscountType = line => {
  if (line?.discount_type_id) return line.discount_type_id;
  if (typeof line?.discount_type === "string") return line.discount_type;
  return line?.discount_type?.id || "percentage";
};

/**
 * Discount taken off a line's pre-tax base (quantity × unit price).
 * Percentage discounts are capped at 100%. Fixed discounts cannot exceed the base.
 * Lines with no discount contribute 0, so callers that do not collect a discount are unchanged.
 */
export const getLineDiscountAmount = (baseAmount, line) => {
  const raw = Number(line?.discount);
  const base = Number(baseAmount);
  if (!Number.isFinite(raw) || raw <= 0 || !Number.isFinite(base) || base <= 0) {
    return 0;
  }
  if (lineDiscountType(line) === "fixed_amount") {
    return Math.min(raw, base);
  }
  return base * (Math.min(raw, 100) / 100);
};

const splitDiscountedLine = (line, rate) => {
  const qty = Number(line?.qty || line?.ordered_qty || line?.quantity) || 0;
  const unitPrice = Number(line?.unit_price) || 0;
  const base = qty * unitPrice;
  const discountAmount = getLineDiscountAmount(base, line);
  const lineSubtotal = base - discountAmount;

  let subTotal = 0;
  let taxAmount = 0;

  if (line?.amount_type_id === "inclusive") {
    taxAmount = lineSubtotal - lineSubtotal / (1 + rate / 100);
    subTotal = lineSubtotal - taxAmount;
  } else if (line?.amount_type_id === "no_tax") {
    subTotal = lineSubtotal;
  } else {
    subTotal = lineSubtotal;
    taxAmount = lineSubtotal * (rate / 100);
  }

  return { subTotal, taxAmount, discountAmount, totalAmount: subTotal + taxAmount };
};

export const useCalculateTotals = lines_data => {
  const { data: taxRates } = useGetTaxSelectionQuery();

  const totals = useMemo(() => {
    let taxTotal = 0;
    let subTotal = 0;
    let discountTotal = 0;

    const safeLines = Array.isArray(lines_data) ? lines_data : [];

    const mapTaxRates = {};
    if (Array.isArray(taxRates)) {
      taxRates.forEach(({ id, rate }) => {
        mapTaxRates[id] = parseFloat(rate ?? 0);
      });
    }

    safeLines.forEach(line => {
      if (
        line?.is_deleted ||
        line?.product_id === "lastLine" ||
        line?.product === "lastLine"
      ) {
        return;
      }
      const rate = mapTaxRates[line.tax_rate_id] || 0;
      const amounts = splitDiscountedLine(line, rate);
      subTotal += amounts.subTotal;
      taxTotal += amounts.taxAmount;
      discountTotal += amounts.discountAmount;
    });

    const grandTotal = subTotal + taxTotal;

    return { subTotal, taxTotal, discountTotal, grandTotal };
  }, [lines_data, taxRates]);

  return totals;
};

export const useCalculateLineTotals = line => {
  const { data: taxRates } = useGetTaxSelectionQuery();

  return useMemo(() => {
    const taxId = line?.tax_rate_id;
    const taxObj = Array.isArray(taxRates) ? taxRates.find(t => t.id === taxId) : null;
    const rate = taxObj ? parseFloat(taxObj.rate ?? 0) : 0;
    const { subTotal, taxAmount, discountAmount, totalAmount } = splitDiscountedLine(line, rate);
    return { subTotal, taxAmount, discountAmount, totalAmount };
  }, [line, taxRates]);
};
